import React from 'react';
import { Link } from 'react-router-dom';
import { Content, Language } from '@/types';
import { getLocalizedPath } from '@/lib/utils';
import { CookieManager } from '@/components/global/CookieManager';
import { Globe, Check, Search, X, Sun, Moon, ShieldCheck, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface FooterProps {
  content: Content['footer'];
  lang: Language;
  setLang?: (lang: Language) => void;
  theme?: 'light' | 'dark';
  toggleTheme?: () => void;
  showLogo?: boolean;
  isB2B?: boolean;
}

// Custom Link Component for Premium Hover Effect
const FooterLink: React.FC<{ to: string; lang?: Language; children: React.ReactNode }> = ({ to, lang, children }) => (
  <Link
    to={getLocalizedPath(to, lang || 'en')}
    className="transition-colors duration-200 text-[#64748d] dark:text-slate-400 hover:text-[#0d253d] dark:hover:text-white text-[13px] font-normal leading-relaxed"
  >
    {children}
  </Link>
);

const FooterExternalLink: React.FC<{ href: string; children: React.ReactNode }> = ({ href, children }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="transition-colors duration-200 text-[#64748d] dark:text-slate-400 hover:text-[#0d253d] dark:hover:text-white text-[13px] font-normal leading-relaxed inline-flex items-center gap-1 group"
  >
    <span>{children}</span>
    <span className="text-[11px] opacity-70 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all">↗</span>
  </a>
);


const Footer: React.FC<FooterProps> = ({ content, lang, setLang, theme, toggleTheme, isB2B }) => {
  const textRef = React.useRef<HTMLHeadingElement>(null);
  const [isFooterHovered, setIsFooterHovered] = React.useState(false);
  const [isTextHovered, setIsTextHovered] = React.useState(false);
  
  // Language Modal State
  const [isLangModalOpen, setIsLangModalOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');

  // Mouse position state with smooth damping
  const [mousePos, setMousePos] = React.useState({ x: 50, y: 50 });
  const [smoothPos, setSmoothPos] = React.useState({ x: 50, y: 50 });

  React.useEffect(() => {
    let animationFrameId: number;
    let targetX = 50;
    let targetY = 50;
    let currentX = 50;
    let currentY = 50;

    const handleMouseMove = (e: MouseEvent) => {
      const footer = document.getElementById('footer');
      if (!footer) return;

      const rect = footer.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;

      targetX = Math.max(0, Math.min(100, x));
      targetY = Math.max(0, Math.min(100, y));
    };

    window.addEventListener('mousemove', handleMouseMove);

    const updateSmoothPosition = () => {
      // Heavy luxury damping effect (Lerp factor = 0.055)
      currentX += (targetX - currentX) * 0.055;
      currentY += (targetY - currentY) * 0.055;

      setSmoothPos({ x: currentX, y: currentY });
      animationFrameId = requestAnimationFrame(updateSmoothPosition);
    };

    animationFrameId = requestAnimationFrame(updateSmoothPosition);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  // Lock body scroll when language modal is open
  React.useEffect(() => {
    if (isLangModalOpen) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [isLangModalOpen]);

  const footerDirectories = {
    en: [
      {
        title: "Products",
        links: [
          { name: "Nova (Core Engine)", path: "/products/nova" },
          { name: "GeoAgent", path: "/products/geo" },
          { name: "CropLens", path: "/products/croplens" },
          { name: "DroughtWatch", path: "/products/droughtwatch" },
          { name: "AgroScore", path: "/products/agroscore" },
          { name: "Nova Workspace", path: "/nova-workspace" },
          { name: "Desktop App", path: "/nova/download" },
          { name: "Pricing", path: "/nova/pricing" },
        ]
      },
      {
        title: "Solutions",
        links: [
          { name: "Overview", path: "/solutions" },
          { name: "Enterprise", path: "/solutions/enterprise" },
          { name: "Urban Planning", path: "/solutions/urban" },
          { name: "Agriculture", path: "/solutions/agri" },
          { name: "Defense & Intel", path: "/solutions/defense" },
          { name: "Logistics & Fleet", path: "/solutions/logistics" },
          { name: "Customer Stories", path: "/case-studies" },
        ]
      },
      {
        title: "Developers",
        links: [
          { name: "Documentation", path: "/docs" },
          { name: "SDKs & Libraries", path: "/sdk" },
          { name: "GIS Integrations", path: "/integrations" },
          { name: "Developer Portal", path: "/portal" },
          { name: "Desktop Engine", path: "/features/desktop" },
          { name: "Code Examples", path: "https://github.com/vexl-gis", isExternal: true },
          { name: "Discord Community", path: "https://discord.gg/vexl", isExternal: true },
          { name: "System Status", path: "https://status.vexl-gis.tech", isExternal: true },
        ]
      },
      {
        title: "Nova Docs",
        links: [
          { name: "Getting Started", path: "/nova/docs/getting-started" },
          { name: "Spatial Formats", path: "/nova/docs/spatial-data" },
          { name: "Prompting Guide", path: "/nova/docs/prompt-guide" },
          { name: "Spatial Memory", path: "/nova/docs/memory" },
          { name: "ArcPy Scripting", path: "/nova/docs/arcpy-execution" },
          { name: "Deep Research", path: "/nova/docs/deep-research" },
          { name: "Academic DOCX", path: "/nova/docs/docx-generator" },
          { name: "Tools Library", path: "/nova/docs/tools" },
          { name: "FAQ", path: "/nova/docs/faq" },
        ]
      },
      {
        title: "Research",
        links: [
          { name: "Research Overview", path: "/research/overview" },
          { name: "Publications Index", path: "/research/index" },
          { name: "Model Benchmarks", path: "/benchmarks" },
          { name: "Research Residency", path: "/research/residency" },
          { name: "Vision & Roadmap", path: "/vision" },
          { name: "Trust & Safety", path: "/safety" },
          { name: "Data Security", path: "/security" },
        ]
      },
      {
        title: "Company",
        links: [
          { name: "About Us", path: "/about" },
          { name: "About Nova", path: "/nova/about" },
          { name: "Brand Assets", path: "/brand" },
          { name: "News", path: "/news" },
          { name: "Partners", path: "/partners" },
          { name: "Careers", path: "/careers" },
          { name: "Contact", path: "/contact" },
          { name: "Release Notes", path: "/release-notes" },
          { name: "Help Center", path: "/help" },
        ]
      },
      {
        title: "Terms & Policies",
        links: [
          { name: "Terms of Use", path: "/terms" },
          { name: "Privacy Policy", path: "/privacy" },
          { name: "Other Policies", path: "/policies" },
          { name: "Security & Privacy", path: "/security" },
          { name: "Trust & Safety", path: "/safety" },
          { name: "IP Rights (EGIPA)", path: "/intellectual-property" },
        ]
      }
    ],
    ar: [
      {
        title: "المنتجات",
        links: [
          { name: "نوفا (المحرك المكاني)", path: "/products/nova" },
          { name: "العميل الجغرافي (GeoAgent)", path: "/products/geo" },
          { name: "كروب لينس (CropLens)", path: "/products/croplens" },
          { name: "دراوت ووتش (DroughtWatch)", path: "/products/droughtwatch" },
          { name: "أجرو سكور (AgroScore)", path: "/products/agroscore" },
          { name: "مساحة عمل نوفا (Workspace)", path: "/nova-workspace" },
          { name: "تطبيق سطح المكتب", path: "/nova/download" },
          { name: "الأسعار والخطط", path: "/nova/pricing" },
        ]
      },
      {
        title: "الحلول",
        links: [
          { name: "نظرة شاملة", path: "/solutions" },
          { name: "الشركات والمؤسسات", path: "/solutions/enterprise" },
          { name: "التخطيط العمراني", path: "/solutions/urban" },
          { name: "القطاع الزراعي", path: "/solutions/agri" },
          { name: "الدفاع والاستخبارات", path: "/solutions/defense" },
          { name: "اللوجستيات والإمداد", path: "/solutions/logistics" },
          { name: "قصص العملاء والأثر", path: "/case-studies" },
        ]
      },
      {
        title: "المطورين",
        links: [
          { name: "التوثيق البرمجي", path: "/docs" },
          { name: "مكتبات SDKs", path: "/sdk" },
          { name: "تكاملات نظم المعلومات (GIS)", path: "/integrations" },
          { name: "بوابة المطورين", path: "/portal" },
          { name: "محرك سطح المكتب", path: "/features/desktop" },
          { name: "أمثلة برمجية", path: "https://github.com/vexl-gis", isExternal: true },
          { name: "مجتمع ديسكورد", path: "https://discord.gg/vexl", isExternal: true },
          { name: "حالة النظام والتشغيل", path: "https://status.vexl-gis.tech", isExternal: true },
        ]
      },
      {
        title: "توثيق نوفا",
        links: [
          { name: "دليل البدء", path: "/nova/docs/getting-started" },
          { name: "تنسيقات البيانات المكانية", path: "/nova/docs/spatial-data" },
          { name: "دليل صياغة الأوامر", path: "/nova/docs/prompt-guide" },
          { name: "الذاكرة المكانية", path: "/nova/docs/memory" },
          { name: "تنفيذ أكواد ArcPy", path: "/nova/docs/arcpy-execution" },
          { name: "البحث المكاني العميق", path: "/nova/docs/deep-research" },
          { name: "تقارير DOCX الأكاديمية", path: "/nova/docs/docx-generator" },
          { name: "مكتبة الأدوات", path: "/nova/docs/tools" },
          { name: "الأسئلة الشائعة", path: "/nova/docs/faq" },
        ]
      },
      {
        title: "الأبحاث",
        links: [
          { name: "نظرة عامة على الأبحاث", path: "/research/overview" },
          { name: "فهرس المنشورات العلمية", path: "/research/index" },
          { name: "المقارنات المعيارية", path: "/benchmarks" },
          { name: "برنامج الإقامة البحثية", path: "/research/residency" },
          { name: "الرؤية التقنية والخارطة", path: "/vision" },
          { name: "الأمان والموثوقية", path: "/safety" },
          { name: "أمن وسيادة البيانات", path: "/security" },
        ]
      },
      {
        title: "الشركة",
        links: [
          { name: "من نحن", path: "/about" },
          { name: "عن منظومة نوفا", path: "/nova/about" },
          { name: "أصول الهوية والملفات", path: "/brand" },
          { name: "غرفة الأخبار", path: "/news" },
          { name: "الشركاء والتحالفات", path: "/partners" },
          { name: "الوظائف وفرص العمل", path: "/careers" },
          { name: "اتصل بنا", path: "/contact" },
          { name: "سجل التحديثات", path: "/release-notes" },
          { name: "مركز المساعدة", path: "/help" },
        ]
      },
      {
        title: "الشروط والسياسات",
        links: [
          { name: "شروط الاستخدام", path: "/terms" },
          { name: "سياسة الخصوصية", path: "/privacy" },
          { name: "السياسات الأخرى", path: "/policies" },
          { name: "الأمان والخصوصية", path: "/security" },
          { name: "الموثوقية والأمان", path: "/safety" },
          { name: "الملكية الفكرية (EGIPA)", path: "/intellectual-property" },
        ]
      }
    ]
  }[lang];

  return (
    <footer
      id="footer"
      onMouseEnter={() => setIsFooterHovered(true)}
      onMouseLeave={() => setIsFooterHovered(false)}
      dir={lang === 'ar' ? 'rtl' : 'ltr'}
      className="bg-white dark:bg-black pt-24 pb-0 border-t border-[#e3e8ee] dark:border-white/[0.06] relative z-10 transition-colors duration-500 overflow-hidden"
    >
      <div className="max-w-[1450px] mx-auto px-6 md:px-12 relative z-10">
        
        {/* Stripe-Inspired Directory Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-8 lg:gap-5 xl:gap-7 mb-16">
          {footerDirectories.map((column) => (
            <div key={column.title} className="flex flex-col gap-2.5">
              <span className="text-slate-900 dark:text-white text-[13px] font-semibold tracking-normal mb-2.5">
                {column.title}
              </span>
              {column.links.map((link) => (
                link.isExternal ? (
                  <FooterExternalLink key={link.name} href={link.path}>
                    {link.name}
                  </FooterExternalLink>
                ) : (
                  <FooterLink key={link.name} to={link.path} lang={lang}>
                    {link.name}
                  </FooterLink>
                )
              ))}
            </div>
          ))}
        </div>

        {/* ── Sovereign Intellectual Property Verification Banner ──────────────── */}
        <div className="flex flex-wrap items-center justify-between gap-4 py-3.5 px-5 sm:px-6 rounded-xl bg-black/[0.02] dark:bg-white/[0.02] border border-black/[0.05] dark:border-white/[0.05] mb-8 text-xs">
          <div className="flex items-center gap-2.5 text-slate-600 dark:text-slate-400 font-normal">
            <ShieldCheck size={16} className="text-emerald-500 shrink-0" />
            <span>
              {lang === 'ar'
                ? 'منظومة برمجية مسجلة ومحمية الملكية الفكرية · إيداع رقم 5007 لدى الجهاز المصري للملكية الفكرية (EGIPA)'
                : 'Protected Intellectual Property · EGIPA Deposit No. 5007 · Egyptian Intellectual Property Authority'}
            </span>
          </div>
          <Link
            to={getLocalizedPath("/intellectual-property", lang)}
            className="text-[#0E93A3] hover:underline font-semibold text-xs flex items-center gap-1 shrink-0"
          >
            <span>{lang === 'ar' ? 'التحقق من التوثيق' : 'Verify Deposit'}</span>
            <ChevronRight size={13} className={lang === 'ar' ? 'rotate-180' : ''} />
          </Link>
        </div>

        <div className="w-full h-px bg-[#e3e8ee] dark:bg-white/[0.08] mb-8" />

        {/* Bottom Bar: Left Socials, Center Copyright & Cookies, Right Language & Theme */}
        <div className="flex flex-col lg:flex-row justify-between items-center w-full text-xs text-[#64748d] dark:text-slate-400 gap-6 font-medium">
          
          {/* Left: Social Media Icon Buttons Dock */}
          <div className="flex items-center justify-center lg:justify-start gap-2 order-2 lg:order-1">
            {[
              {
                label: 'X (Twitter)',
                href: 'https://x.com/vexl_tech',
                icon: (
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                )
              },
              {
                label: 'GitHub',
                href: 'https://github.com/vexl-gis',
                icon: (
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
                  </svg>
                )
              },
              {
                label: 'LinkedIn',
                href: 'https://www.linkedin.com/company/vexl-studio',
                icon: (
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                )
              },
              {
                label: 'Instagram',
                href: 'https://www.instagram.com/vexl_official/',
                icon: (
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                  </svg>
                )
              },
              {
                label: 'WhatsApp',
                href: 'https://wa.me/201279298987',
                icon: (
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
                  </svg>
                )
              },
              {
                label: 'Email',
                href: 'mailto:vexl.gis@gmail.com',
                icon: (
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect width="20" height="16" x="2" y="4" rx="2" />
                    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                  </svg>
                )
              },
              {
                label: 'Phone (+201279298987)',
                href: 'tel:+201279298987',
                icon: (
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                  </svg>
                )
              }
            ].map((social) => (
              <a
                key={social.label}
                href={social.href}
                target={social.href.startsWith('http') ? '_blank' : undefined}
                rel={social.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                aria-label={social.label}
                title={social.label}
                className="w-8 h-8 rounded-full flex items-center justify-center border border-slate-200 dark:border-white/[0.08] bg-[#f8fafc] dark:bg-slate-900/60 text-slate-600 dark:text-slate-400 hover:text-slate-950 dark:hover:text-white hover:border-slate-300 dark:hover:border-white/20 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all duration-200 hover:scale-105 shadow-sm"
              >
                {social.icon}
              </a>
            ))}
          </div>

          {/* Center: Copyright & Manage Cookies */}
          <div className="flex flex-wrap gap-2.5 items-center justify-center text-[12px] text-[#64748d] dark:text-slate-400 font-medium order-1 lg:order-2">
            <span className="text-[#0d253d] dark:text-slate-300 font-normal">{content.copyright}</span>
            <span className="text-[#cbd5e1] dark:text-white/20">•</span>
            <button
              onClick={() => window.dispatchEvent(new Event('open-cookie-manager'))}
              className="hover:text-[#0d253d] dark:hover:text-white transition-colors cursor-pointer uppercase font-medium"
            >
              {lang === 'ar' ? 'إدارة ملفات تعريف الارتباط' : 'MANAGE COOKIES'}
            </button>
          </div>

          {/* Right: Language Selector & Theme Toggle Pills */}
          <div className="flex items-center justify-center lg:justify-end gap-3 order-3">
            {/* Language Selection Pill (OpenAI Style) */}
            {setLang && (
              <button
                onClick={() => setIsLangModalOpen(true)}
                className="flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-slate-200 dark:border-slate-800 bg-[#f8fafc] dark:bg-slate-900/60 hover:bg-[#f1f5f9] dark:hover:bg-slate-900/80 text-slate-800 dark:text-slate-200 transition-all text-xs font-medium"
              >
                <Globe size={13} className="opacity-70" />
                <span>{lang === 'ar' ? 'العربية' : 'English United States'}</span>
              </button>
            )}

            {/* Theme Toggle Pill */}
            {toggleTheme && theme && (
              <button
                onClick={toggleTheme}
                className="flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-slate-200 dark:border-slate-800 bg-[#f8fafc] dark:bg-slate-900/60 hover:bg-[#f1f5f9] dark:hover:bg-slate-900/80 text-slate-800 dark:text-slate-200 transition-all text-xs font-medium"
                aria-label="Toggle Theme"
              >
                {theme === 'dark' ? <Moon size={13} className="opacity-70" /> : <Sun size={13} className="opacity-70" />}
                <span className="capitalize">{theme === 'dark' ? (lang === 'ar' ? 'داكن' : 'Dark') : (lang === 'ar' ? 'مضيء' : 'Light')}</span>
              </button>
            )}
          </div>
        </div>

      </div>

      {/* OpenAI-Style Language Selection Modal */}
      <AnimatePresence>
        {isLangModalOpen && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsLangModalOpen(false)}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm"
            />

            {/* Modal Card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
              transition={{ type: 'spring', duration: 0.4 }}
              className="w-full max-w-[400px] h-[520px] bg-white dark:bg-[#171717] rounded-[24px] border border-slate-200 dark:border-white/[0.08] shadow-2xl flex flex-col overflow-hidden relative z-10"
            >
              {/* Header */}
              <div className="px-6 pt-5 pb-4 flex items-center justify-between border-b border-slate-100 dark:border-white/[0.06]">
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
                  {lang === 'ar' ? 'اختر اللغة' : 'Select language'}
                </h3>
                <button
                  onClick={() => setIsLangModalOpen(false)}
                  className="p-1.5 rounded-full hover:bg-slate-100 dark:hover:bg-white/10 text-slate-500 dark:text-slate-400 transition-colors"
                >
                  <X size={16} />
                </button>
              </div>

              {/* Language List */}
              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-1">
                {[
                  { code: 'en', name: 'English United States', nativeName: 'English' },
                  { code: 'ar', name: 'العربية', nativeName: 'Arabic' }
                ]
                  .filter(opt =>
                    opt.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    opt.nativeName.toLowerCase().includes(searchQuery.toLowerCase())
                  )
                  .map((opt) => {
                    const isActive = lang === opt.code;
                    return (
                      <button
                        key={opt.code}
                        onClick={() => {
                          if (setLang) setLang(opt.code as Language);
                          setIsLangModalOpen(false);
                        }}
                        className={`w-full flex items-center justify-between px-4 py-3 rounded-xl hover:bg-slate-100 dark:hover:bg-white/5 transition-all text-left ${
                          lang === 'ar' ? 'text-right flex-row-reverse' : ''
                        }`}
                      >
                        <div className={`flex flex-col ${lang === 'ar' ? 'items-end' : 'items-start'}`}>
                          <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                            {opt.name}
                          </span>
                          <span className="text-xs text-slate-500 dark:text-slate-400 font-light mt-0.5">
                            {opt.nativeName}
                          </span>
                        </div>
                        {isActive && (
                          <div className="w-5 h-5 rounded-full bg-slate-950 dark:bg-white text-white dark:text-black flex items-center justify-center">
                            <Check size={12} strokeWidth={3} />
                          </div>
                        )}
                      </button>
                    );
                  })}
                {/* No results fallback */}
                {[
                  { code: 'en', name: 'English United States', nativeName: 'English' },
                  { code: 'ar', name: 'العربية', nativeName: 'Arabic' }
                ].filter(opt =>
                  opt.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  opt.nativeName.toLowerCase().includes(searchQuery.toLowerCase())
                ).length === 0 && (
                  <div className="text-center py-8 text-xs text-slate-400 dark:text-slate-500">
                    {lang === 'ar' ? 'لا توجد نتائج' : 'No results found'}
                  </div>
                )}
              </div>

              {/* Search Footer */}
              <div className="p-4 border-t border-slate-100 dark:border-white/[0.06] bg-slate-50/50 dark:bg-[#151515]">
                <div className="relative">
                  <Search className={`absolute ${lang === 'ar' ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 w-4 h-4`} />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={lang === 'ar' ? 'بحث...' : 'Search'}
                    className={`w-full ${lang === 'ar' ? 'pr-9 pl-4 text-right' : 'pl-9 pr-4'} py-2 rounded-xl bg-white dark:bg-[#202020] border border-slate-200 dark:border-white/5 focus:outline-none focus:border-slate-400 dark:focus:border-white/20 text-sm text-slate-800 dark:text-slate-200 placeholder-slate-400 dark:placeholder-slate-500 shadow-sm`}
                  />
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Giant Interactive Brand Wordmark (Bugatti-style reveal) */}
      <div
        dir="ltr"
        className="w-full flex items-end justify-center pointer-events-none mt-4 md:mt-6 overflow-hidden select-none relative"
        style={{
          height: 'clamp(130px, 20vw, 340px)',
          minHeight: '120px',
        }}
      >
        <h1
          ref={textRef}
          onMouseEnter={() => setIsTextHovered(true)}
          onMouseLeave={() => setIsTextHovered(false)}
          className={`
            font-black uppercase text-center select-none pointer-events-auto cursor-default transition-opacity duration-700 m-0 p-0 leading-none whitespace-nowrap
            ${isFooterHovered ? 'opacity-100' : 'opacity-0'}
          `}
          style={{
            fontSize: 'clamp(155px, 29.5vw, 500px)',
            lineHeight: '0.80',
            letterSpacing: '0.08em',
            textIndent: '0.08em', // Exact optical centering offset
            backgroundImage: `
              linear-gradient(to bottom, var(--vexl-footer-glow), var(--vexl-footer-glow)),
              url("data:image/svg+xml,%3Csvg viewBox='0 0 250 250' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.55'/%3E%3C/svg%3E")
            `,
            backgroundSize: '100% 100%, 150px 150px',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            transform: 'translateY(12%)',
            maskImage: 'radial-gradient(circle at var(--mouse-x, 50%) var(--mouse-y, 50%), black 0%, transparent 48%)',
            WebkitMaskImage: 'radial-gradient(circle at var(--mouse-x, 50%) var(--mouse-y, 50%), black 0%, transparent 48%)',
            '--mouse-x': `${smoothPos.x}%`,
            '--mouse-y': `${smoothPos.y}%`,
          } as React.CSSProperties}
        >
          VEXL
        </h1>
      </div>

      {/* Global Cookie Manager Modal/Banner */}
      <CookieManager lang={lang} />
    </footer>
  );
};

export default Footer;
